import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePartidas } from '@/hooks/usePartidas';
import { useActualizarSubPartida, useSubPartida } from '@/hooks/useSubPartidas';
import type { SubPartidaPatch, TipoCasa } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const TIPOS_CASA: TipoCasa[] = ['1N-Techo', '1N-Azotea', '2N-Techo', '2N-Azotea'];

interface Props {
  /** Si tiene valor, abre el modal con el detalle de esa sub-partida. */
  subPartidaId: number | null;
  onClose: () => void;
}

interface FormState {
  codigo: string;
  nombre: string;
  partida_id: string;
  sprint_numero: string;
  es_critica: boolean;
  descripcion: string;
  activo: boolean;
  tipos_casa: Set<TipoCasa>;
}

export function EditarSubPartidaDialog({ subPartidaId, onClose }: Props) {
  const detalleQ = useSubPartida(subPartidaId ?? undefined);
  const partidasQ = usePartidas();
  const mutation = useActualizarSubPartida();

  const [form, setForm] = useState<FormState | null>(null);

  // Inicializar form cuando llega el detalle
  useEffect(() => {
    const d = detalleQ.data?.data;
    if (!d) return;
    setForm({
      codigo: d.codigo,
      nombre: d.nombre,
      partida_id: String(d.partida_id),
      sprint_numero: String(d.sprint_numero),
      es_critica: d.es_critica,
      descripcion: d.descripcion ?? '',
      activo: d.activo,
      tipos_casa: new Set(d.tipos_casa),
    });
  }, [detalleQ.data]);

  const abierto = subPartidaId !== null;
  const detalle = detalleQ.data?.data;
  const partidas = partidasQ.data?.data ?? [];

  function handleClose(open: boolean) {
    if (!open && !mutation.isPending) {
      onClose();
      setForm(null);
    }
  }

  function toggleTipoCasa(tc: TipoCasa) {
    if (!form) return;
    const next = new Set(form.tipos_casa);
    if (next.has(tc)) next.delete(tc);
    else next.add(tc);
    setForm({ ...form, tipos_casa: next });
  }

  async function handleGuardar() {
    if (!form || !detalle || !subPartidaId) return;

    // Construir parche solo con campos que cambiaron
    const parche: SubPartidaPatch = {};
    if (form.codigo !== detalle.codigo) parche.codigo = form.codigo.trim();
    if (form.nombre !== detalle.nombre) parche.nombre = form.nombre.trim();
    if (Number(form.partida_id) !== detalle.partida_id) parche.partida_id = Number(form.partida_id);
    if (Number(form.sprint_numero) !== detalle.sprint_numero)
      parche.sprint_numero = Number(form.sprint_numero);
    if (form.es_critica !== detalle.es_critica) parche.es_critica = form.es_critica;
    if (form.activo !== detalle.activo) parche.activo = form.activo;
    const descActual = detalle.descripcion ?? '';
    if (form.descripcion !== descActual) parche.descripcion = form.descripcion.trim() || null;

    const actuales = new Set(detalle.tipos_casa);
    const cambioTipos =
      form.tipos_casa.size !== actuales.size || [...form.tipos_casa].some((t) => !actuales.has(t));
    if (cambioTipos) parche.tipos_casa = Array.from(form.tipos_casa);

    if (Object.keys(parche).length === 0) {
      toast.info('No hay cambios para guardar');
      return;
    }

    try {
      await mutation.mutateAsync({ id: subPartidaId, parche });
      toast.success('Sub-partida actualizada', {
        description: `${form.codigo} — ${form.nombre}`,
      });
      onClose();
      setForm(null);
    } catch (e) {
      toast.error('Error al guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Editar sub-partida</DialogTitle>
          <DialogDescription>
            {detalle ? (
              <span className="font-mono text-xs">
                {detalle.codigo} · id {detalle.id}
              </span>
            ) : (
              'Cargando…'
            )}
          </DialogDescription>
        </DialogHeader>

        {(detalleQ.isLoading || !form) && (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            Cargando datos…
          </div>
        )}

        {form && detalle && (
          <div className="space-y-4">
            {/* Código + Nombre */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ed-codigo" className="mb-1.5 block text-xs">
                  Código
                </Label>
                <Input
                  id="ed-codigo"
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  className="font-mono"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="ed-nombre" className="mb-1.5 block text-xs">
                  Nombre
                </Label>
                <Input
                  id="ed-nombre"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>
            </div>

            {/* Partida + Sprint */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label htmlFor="ed-partida" className="mb-1.5 block text-xs">
                  Partida
                </Label>
                <Select
                  value={form.partida_id}
                  onValueChange={(v) => setForm({ ...form, partida_id: v })}
                >
                  <SelectTrigger id="ed-partida">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {partidas.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        <span className="font-mono text-xs text-muted-foreground">{p.codigo}</span>{' '}
                        {p.nombre}{' '}
                        <span className="text-xs text-muted-foreground">({p.grupo_codigo})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ed-sprint" className="mb-1.5 block text-xs">
                  Sprint
                </Label>
                <Input
                  id="ed-sprint"
                  type="number"
                  min={1}
                  max={50}
                  value={form.sprint_numero}
                  onChange={(e) => setForm({ ...form, sprint_numero: e.target.value })}
                />
              </div>
            </div>

            {/* Tipos de casa */}
            <div>
              <Label className="mb-2 block text-xs">Aplica a tipos de casa</Label>
              <div className="grid grid-cols-2 gap-2">
                {TIPOS_CASA.map((tc) => {
                  const id = `ed-tc-${tc}`;
                  return (
                    <label
                      key={tc}
                      htmlFor={id}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm hover:bg-zinc-50"
                    >
                      <Checkbox
                        id={id}
                        checked={form.tipos_casa.has(tc)}
                        onCheckedChange={() => toggleTipoCasa(tc)}
                      />
                      <span className="font-mono">{tc}</span>
                    </label>
                  );
                })}
              </div>
              {form.tipos_casa.size === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  ⚠️ Una sub-partida sin tipos de casa no aplicará a ninguna obra.
                </p>
              )}
            </div>

            {/* Descripción */}
            <div>
              <Label htmlFor="ed-desc" className="mb-1.5 block text-xs">
                Descripción (opcional)
              </Label>
              <Textarea
                id="ed-desc"
                rows={2}
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              />
            </div>

            {/* Switches */}
            <div className="flex flex-col gap-3 border-t border-border pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ed-critica" className="text-sm">
                    Crítica
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Bloquea el avance del sprint si está pendiente.
                  </p>
                </div>
                <Switch
                  id="ed-critica"
                  checked={form.es_critica}
                  onCheckedChange={(v) => setForm({ ...form, es_critica: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ed-activo" className="text-sm">
                    Activa
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Si está inactiva no aparece para registrar avances.
                  </p>
                </div>
                <Switch
                  id="ed-activo"
                  checked={form.activo}
                  onCheckedChange={(v) => setForm({ ...form, activo: v })}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={!form || mutation.isPending || form.tipos_casa.size === 0}
          >
            {mutation.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : (
              'Guardar cambios'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
